/**
 * File validation utility functions
 * Reusable across all components with file uploads
 */

/**
 * Validates file type based on MIME type and file extension
 * @param {File} file - The file to validate
 * @param {Object} options - Validation options
 * @param {Array} options.allowedTypes - Array of allowed MIME types
 * @param {Array} options.allowedExtensions - Array of allowed file extensions
 * @param {string} options.errorMessage - Custom error message
 * @returns {Object} Validation result with valid boolean and message
 */
export const validateFileType = (file, options = {}) => {
  // Default allowed types
  const defaultAllowedTypes = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "application/pdf",
  ];

  const defaultAllowedExtensions = [".jpg", ".jpeg", ".png", ".pdf"];

  const defaultErrorMessage =
    "Jenis fail tidak dibenarkan. Sila muat naik fail gambar (JPG, JPEG, PNG) atau PDF sahaja.";

  // Use provided options or defaults
  const allowedTypes = options.allowedTypes || defaultAllowedTypes;
  const allowedExtensions =
    options.allowedExtensions || defaultAllowedExtensions;
  const errorMessage = options.errorMessage || defaultErrorMessage;

  // Check MIME type
  if (!allowedTypes.includes(file.type)) {
    return {
      valid: false,
      message: errorMessage,
    };
  }

  // Check file extension
  const fileName = file.name.toLowerCase();
  const hasValidExtension = allowedExtensions.some((ext) =>
    fileName.endsWith(ext)
  );

  if (!hasValidExtension) {
    return {
      valid: false,
      message:
        "Sambungan fail tidak dibenarkan. Sila muat naik fail gambar (JPG, JPEG, PNG) atau PDF sahaja.",
    };
  }

  return { valid: true };
};

/**
 * Validates file size
 * @param {File} file - The file to validate
 * @param {number} maxSizeMB - Maximum file size in MB
 * @param {string} errorMessage - Custom error message
 * @returns {Object} Validation result with valid boolean and message
 */
export const validateFileSize = (file, maxSizeMB = 10, errorMessage = null) => {
  const maxSizeBytes = maxSizeMB * 1024 * 1024; // Convert MB to bytes

  if (file.size > maxSizeBytes) {
    return {
      valid: false,
      message:
        errorMessage ||
        `Saiz fail terlalu besar. Saiz maksimum ialah ${maxSizeMB}MB.`,
    };
  }

  return { valid: true };
};

/**
 * Comprehensive file validation combining type and size checks
 * @param {File} file - The file to validate
 * @param {Object} options - Validation options
 * @returns {Object} Validation result with valid boolean and message
 */
export const validateFile = (file, options = {}) => {
  // Type validation
  const typeValidation = validateFileType(file, options);
  if (!typeValidation.valid) {
    return typeValidation;
  }

  // Size validation
  const sizeValidation = validateFileSize(
    file,
    options.maxSizeMB,
    options.sizeErrorMessage
  );
  if (!sizeValidation.valid) {
    return sizeValidation;
  }

  return { valid: true };
};

/**
 * Shortens filename for display
 * @param {string} filename - The filename to shorten
 * @param {number} maxLength - Maximum length before shortening
 * @returns {string} Shortened filename
 */
export const shortenFilename = (filename, maxLength = 8) => {
  const [name, extension] = filename.split(".");
  if (name.length <= maxLength) {
    return filename;
  }
  return `${name.substring(0, maxLength - 3)}...${
    extension ? `.${extension}` : ""
  }`;
};

/**
 * Gets file extension from filename
 * @param {string} filename - The filename
 * @returns {string} File extension (without dot)
 */
export const getFileExtension = (filename) => {
  return filename.split(".").pop().toLowerCase();
};

/**
 * Checks if file is an image
 * @param {File} file - The file to check
 * @returns {boolean} True if file is an image
 */
export const isImageFile = (file) => {
  return file.type.startsWith("image/");
};

/**
 * Checks if file is a PDF
 * @param {File} file - The file to check
 * @returns {boolean} True if file is a PDF
 */
export const isPdfFile = (file) => {
  return file.type === "application/pdf";
};

/**
 * Predefined validation configurations for common use cases
 */
export const validationConfigs = {
  // Default configuration for most uploads
  default: {
    allowedTypes: ["image/jpeg", "image/jpg", "image/png", "application/pdf"],
    allowedExtensions: [".jpg", ".jpeg", ".png", ".pdf"],
    maxSizeMB: 10,
    errorMessage:
      "Jenis fail tidak dibenarkan. Sila muat naik fail gambar (JPG, JPEG, PNG) atau PDF sahaja.",
  },

  // Images only
  imagesOnly: {
    allowedTypes: ["image/jpeg", "image/jpg", "image/png"],
    allowedExtensions: [".jpg", ".jpeg", ".png"],
    maxSizeMB: 5,
    errorMessage:
      "Jenis fail tidak dibenarkan. Sila muat naik fail gambar (JPG, JPEG, PNG) sahaja.",
  },

  // PDF only
  pdfOnly: {
    allowedTypes: ["application/pdf"],
    allowedExtensions: [".pdf"],
    maxSizeMB: 10,
    errorMessage:
      "Jenis fail tidak dibenarkan. Sila muat naik fail PDF sahaja.",
  },

  // Documents (PDF and images)
  documents: {
    allowedTypes: ["image/jpeg", "image/jpg", "image/png", "application/pdf"],
    allowedExtensions: [".jpg", ".jpeg", ".png", ".pdf"],
    maxSizeMB: 15,
    errorMessage:
      "Jenis fail tidak dibenarkan. Sila muat naik fail gambar (JPG, JPEG, PNG) atau PDF sahaja.",
  },
};
